import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import {
  MessageSquare, Send, Loader2, X, Bot, User, Sparkles,
  Minimize2, Maximize2, Trash2, ChevronDown, Mic, MicOff,
  AlertTriangle, TrendingUp, Truck, MapPin, Clock, DollarSign,
  Calendar, FileText, Download, RefreshCw, ChevronRight,
  Package, Users, BarChart3, Zap, Play, CheckCircle, XCircle,
  Lightbulb, Search, ClipboardList, PlusCircle
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';

// ReactMarkdown component overrides — hoisted out of render so React doesn't
// re-create them on every parent render (avoids react/no-unstable-nested-components).
const MD_P = ({ children }) => <p className="mb-2 last:mb-0 text-sm">{children}</p>;
const MD_UL = ({ children }) => <ul className="list-disc pl-4 mb-2 text-sm">{children}</ul>;
const MD_OL = ({ children }) => <ol className="list-decimal pl-4 mb-2 text-sm">{children}</ol>;
const MD_LI = ({ children }) => <li className="mb-1">{children}</li>;
const MD_STRONG = ({ children }) => <strong className="font-semibold text-white">{children}</strong>;
const MD_CODE = ({ children }) => <code className="bg-slate-700 px-1 rounded text-xs">{children}</code>;
const MD_PRE = ({ children }) => <pre className="bg-slate-900 p-2 rounded text-xs overflow-x-auto my-2">{children}</pre>;
const MD_TABLE = ({ children }) => <table className="min-w-full border-collapse my-2 text-xs">{children}</table>;
const MD_TH = ({ children }) => <th className="border border-slate-600 px-2 py-1 bg-slate-700 text-left">{children}</th>;
const MD_TD = ({ children }) => <td className="border border-slate-600 px-2 py-1">{children}</td>;
const MARKDOWN_COMPONENTS = {
  p: MD_P, ul: MD_UL, ol: MD_OL, li: MD_LI, strong: MD_STRONG,
  code: MD_CODE, pre: MD_PRE, table: MD_TABLE, th: MD_TH, td: MD_TD,
};

// Action type icons and colors
const ACTION_CONFIG = {
  create_shipment: { icon: Package, color: 'blue', label: 'Create Shipment' },
  update_shipment_status: { icon: RefreshCw, color: 'amber', label: 'Update Status' },
  assign_driver_to_load: { icon: Truck, color: 'green', label: 'Assign Driver' },
  schedule_appointment: { icon: Calendar, color: 'purple', label: 'Schedule Appointment' },
  create_otr_load: { icon: Truck, color: 'blue', label: 'Create OTR Load' },
  book_otr_load: { icon: CheckCircle, color: 'green', label: 'Book OTR Load' },
  update_otr_status: { icon: RefreshCw, color: 'amber', label: 'Update OTR Status' },
  create_local_load: { icon: MapPin, color: 'purple', label: 'Create Local Load' },
  update_local_status: { icon: RefreshCw, color: 'amber', label: 'Update Local Status' },
  update_driver_hos: { icon: Clock, color: 'orange', label: 'Update HOS' },
  approve_payout: { icon: DollarSign, color: 'green', label: 'Approve Payout' },
  create_time_entry: { icon: Clock, color: 'blue', label: 'Create Time Entry' },
};

// Action Confirmation Card Component
const ActionConfirmationCard = ({ action, onConfirm, onCancel, isExecuting }) => {
  const config = ACTION_CONFIG[action.action_type] || { icon: Play, color: 'gray', label: action.action_type };
  const Icon = config.icon;
  
  const colorClasses = {
    blue: 'from-blue-600/20 to-blue-700/20 border-blue-500/50',
    green: 'from-green-600/20 to-green-700/20 border-green-500/50',
    amber: 'from-amber-600/20 to-amber-700/20 border-amber-500/50',
    purple: 'from-purple-600/20 to-purple-700/20 border-purple-500/50',
    orange: 'from-orange-600/20 to-orange-700/20 border-orange-500/50',
    gray: 'from-slate-600/20 to-slate-700/20 border-slate-500/50',
  };
  
  const iconColors = {
    blue: 'text-blue-400',
    green: 'text-green-400',
    amber: 'text-amber-400',
    purple: 'text-purple-400',
    orange: 'text-orange-400',
    gray: 'text-slate-400',
  };
  
  return (
    <div className={`mt-3 p-4 rounded-lg border bg-gradient-to-r ${colorClasses[config.color]}`}>
      <div className="flex items-start gap-3">
        <div className={`p-2 rounded-lg bg-slate-800 ${iconColors[config.color]}`}>
          <Icon className="h-5 w-5" />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-white font-medium text-sm">{config.label}</span>
            <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/50 text-xs">
              Pending Confirmation
            </Badge>
          </div>
          
          <p className="text-slate-300 text-sm mb-3">{action.description}</p>
          
          {/* Parameters */}
          <div className="bg-slate-900/50 rounded-lg p-3 mb-3">
            <p className="text-xs text-slate-500 mb-2 font-medium">Parameters:</p>
            <div className="grid grid-cols-2 gap-2">
              {Object.entries(action.params || {}).map(([key, value]) => (
                <div key={key} className="text-xs">
                  <span className="text-slate-500">{key.replace(/_/g, ' ')}:</span>
                  <span className="text-slate-300 ml-1">{String(value)}</span>
                </div>
              ))}
            </div>
          </div>
          
          {/* Action Buttons */}
          <div className="flex gap-2">
            <Button
              size="sm"
              onClick={onConfirm}
              disabled={isExecuting}
              className="bg-green-600 hover:bg-green-500 text-white"
              data-testid="confirm-action-btn"
            >
              {isExecuting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                  Executing...
                </>
              ) : (
                <>
                  <CheckCircle className="h-4 w-4 mr-1" />
                  Confirm & Execute
                </>
              )}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={onCancel}
              disabled={isExecuting}
              className="border-slate-600 text-slate-300 hover:bg-slate-800"
              data-testid="cancel-action-btn"
            >
              <XCircle className="h-4 w-4 mr-1" />
              Cancel
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Recommendations Card Component
const RecommendationsCard = ({ recommendations, onAsk }) => {
  if (!recommendations || recommendations.length === 0) return null;
  
  const typeConfig = {
    critical: { color: 'red', icon: AlertTriangle },
    urgent: { color: 'red', icon: AlertTriangle },
    warning: { color: 'amber', icon: AlertTriangle },
    opportunity: { color: 'green', icon: Lightbulb },
    info: { color: 'blue', icon: ClipboardList },
  };
  
  return (
    <div className="mt-3 space-y-2">
      <p className="text-xs text-slate-500 font-medium flex items-center gap-1">
        <Lightbulb className="h-3 w-3" />
        Recommendations
      </p>
      {recommendations.slice(0, 3).map((rec, idx) => {
        const config = typeConfig[rec.type] || typeConfig.info;
        const Icon = config.icon;
        const colorClasses = {
          red: 'border-red-500/30 bg-red-500/10',
          amber: 'border-amber-500/30 bg-amber-500/10',
          green: 'border-green-500/30 bg-green-500/10',
          blue: 'border-blue-500/30 bg-blue-500/10',
        };
        const textColors = {
          red: 'text-red-400',
          amber: 'text-amber-400',
          green: 'text-green-400',
          blue: 'text-blue-400',
        };
        
        return (
          <div
            key={idx}
            className={`p-2 rounded-lg border ${colorClasses[config.color]} cursor-pointer hover:opacity-80 transition-opacity`}
            onClick={() => onAsk(`Tell me more about: ${rec.title}`)}
          >
            <div className="flex items-start gap-2">
              <Icon className={`h-4 w-4 ${textColors[config.color]} flex-shrink-0 mt-0.5`} />
              <div>
                <p className={`text-xs font-medium ${textColors[config.color]}`}>{rec.title}</p>
                <p className="text-xs text-slate-400 mt-0.5">{rec.message}</p>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

const AIAssistant = () => {
  const { api, user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [quickStats, setQuickStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(true);
  const [contextSuggestions, setContextSuggestions] = useState([]);
  const [pendingAction, setPendingAction] = useState(null);
  const [isExecutingAction, setIsExecutingAction] = useState(false);
  const [recommendations, setRecommendations] = useState([]);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const recognitionRef = useRef(null);

  // Initialize speech recognition
  useEffect(() => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = false;
      recognitionRef.current.interimResults = true;
      recognitionRef.current.lang = 'en-US';
      
      recognitionRef.current.onresult = (event) => {
        const transcript = Array.from(event.results)
          .map(result => result[0])
          .map(result => result.transcript)
          .join('');
        setInputMessage(transcript);
      };
      
      recognitionRef.current.onend = () => {
        setIsListening(false);
      };
      
      recognitionRef.current.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        setIsListening(false);
        if (event.error !== 'aborted') {
          toast.error('Voice recognition failed. Please try again.');
        }
      };
    }
    
    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.abort();
      }
    };
  }, []);

  // Toggle voice input
  const toggleVoiceInput = () => {
    if (!recognitionRef.current) {
      toast.error('Voice input not supported in this browser');
      return;
    }
    
    if (isListening) {
      recognitionRef.current.stop();
      setIsListening(false);
    } else {
      recognitionRef.current.start();
      setIsListening(true);
      toast.info('Listening...', { duration: 2000 });
    }
  };

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  // Focus input when chat opens
  useEffect(() => {
    if (isOpen && !isMinimized && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen, isMinimized]);

  // Load chat history and quick stats on open
  useEffect(() => {
    if (isOpen) {
      if (sessionId) {
        loadHistory();
      }
      fetchQuickStats();
    }
  }, [isOpen, sessionId]);

  const fetchQuickStats = async () => {
    setLoadingStats(true);
    try {
      const response = await api.get('/ai-assistant/quick-stats');
      setQuickStats(response.data);
    } catch (error) {
      console.error('Failed to fetch quick stats:', error);
    } finally {
      setLoadingStats(false);
    }
  };

  const loadHistory = async () => {
    try {
      const response = await api.get(`/ai-assistant/history?session_id=${sessionId}&limit=20`);
      const history = response.data || [];

      // Convert history to messages format (reverse since it comes newest first).
      // .toReversed() is the non-mutating array reversal (ES2023) — satisfies
      // the react-hooks/immutability rule.
      const convertedMessages = history.toReversed().flatMap(item => [
        { role: 'user', content: item.user_message, timestamp: item.created_at },
        { role: 'assistant', content: item.ai_response, timestamp: item.created_at },
      ]);

      if (convertedMessages.length > 0) {
        setMessages(convertedMessages);
      }
    } catch (error) {
      console.error('Failed to load chat history:', error);
    }
  };

  // Generate context-aware suggestions based on AI response
  const generateSuggestions = useCallback((response, userQuery) => {
    const lowerResponse = response.toLowerCase();
    const lowerQuery = userQuery.toLowerCase();

    const isDriverRelated = lowerQuery.includes('driver') || lowerResponse.includes('driver');
    const isLoadRelated = lowerQuery.includes('load') || lowerQuery.includes('shipment') || lowerResponse.includes('container');
    const isOtrRelated = lowerQuery.includes('otr') || lowerQuery.includes('load board') || lowerResponse.includes('available load');
    const isHosRelated = lowerQuery.includes('hos') || lowerQuery.includes('hours') || lowerResponse.includes('violation') || lowerResponse.includes('driving');
    const isLocalRelated = lowerQuery.includes('local') || lowerResponse.includes('local load') || lowerResponse.includes('pod');
    const isReportRelated = lowerQuery.includes('report') || lowerQuery.includes('payout') || lowerQuery.includes('pay');
    const isStatusRelated = lowerQuery.includes('status') || lowerQuery.includes('summary');

    // Build the suggestion list functionally (immutable) so the linter's
    // react-hooks/immutability rule (React Compiler) is satisfied.
    const suggestions = [
      ...(isDriverRelated && !lowerQuery.includes('location') ? ['Where is this driver now?'] : []),
      ...(isDriverRelated && !lowerQuery.includes('pay') && !lowerQuery.includes('earning') ? ["Show driver's earnings this month"] : []),
      ...(isDriverRelated && !lowerQuery.includes('hos') ? ["Check this driver's HOS status"] : []),

      ...(isLoadRelated ? ['Show loads due for delivery today', 'Which loads have LFD approaching?'] : []),

      ...(isOtrRelated ? ['Which drivers can take this load?', 'Show all booked OTR loads', 'Find loads by destination state'] : []),

      ...(isHosRelated ? ['Show all HOS violations this week', 'Which drivers are close to limits?', 'Who can drive for 8 more hours?'] : []),

      ...(isLocalRelated ? ['Show all active local deliveries', 'Any missing PODs today?', 'Find nearest driver to pickup'] : []),

      ...(isReportRelated ? ['Export this report to CSV', 'Compare to last month', 'Show detailed breakdown by driver'] : []),

      ...(isStatusRelated ? ['What needs my attention today?', 'Show me any alerts or issues'] : []),
    ];

    // Limit to 3 suggestions
    setContextSuggestions(suggestions.slice(0, 3));
  }, []);

  const handleSendMessage = async (messageOverride = null) => {
    const messageToSend = messageOverride || inputMessage.trim();
    if (!messageToSend || isLoading) return;

    setInputMessage('');
    setShowSuggestions(false);
    setContextSuggestions([]);
    setPendingAction(null);
    
    // Add user message to chat
    setMessages(prev => [...prev, {
      role: 'user',
      content: messageToSend,
      timestamp: new Date().toISOString()
    }]);

    setIsLoading(true);

    try {
      const response = await api.post('/ai-assistant/chat', {
        message: messageToSend,
        session_id: sessionId
      });

      // Set session ID from response if not set
      if (!sessionId && response.data.session_id) {
        setSessionId(response.data.session_id);
      }

      // Check for proposed action
      const proposedAction = response.data.proposed_action;
      
      // Check for recommendations
      const recs = response.data.recommendations;
      if (recs && recs.length > 0) {
        setRecommendations(recs);
      }

      // Clean the response text by removing the ACTION_PROPOSAL block for display
      let cleanedResponse = response.data.response;
      if (proposedAction) {
        // Remove ACTION_PROPOSAL block from display
        cleanedResponse = cleanedResponse.replace(/```[\s\S]*?ACTION_PROPOSAL:[\s\S]*?```/g, '').trim();
        cleanedResponse = cleanedResponse.replace(/ACTION_PROPOSAL:[\s\S]*?\}\s*\}/g, '').trim();
      }

      // Add AI response to chat
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: cleanedResponse,
        timestamp: new Date().toISOString(),
        dataContext: response.data.data_context,
        proposedAction: proposedAction,
        recommendations: recs,
        searchResults: response.data.search_results
      }]);

      // Set pending action if proposed
      if (proposedAction) {
        setPendingAction(proposedAction);
      }

      // Generate follow-up suggestions
      generateSuggestions(response.data.response, messageToSend);

    } catch (error) {
      console.error('AI Assistant error:', error);
      toast.error('Failed to get response from AI assistant');
      
      // Add error message
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Sorry, I encountered an error processing your request. Please try again.',
        timestamp: new Date().toISOString(),
        isError: true
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  // Execute confirmed action
  const handleExecuteAction = async () => {
    if (!pendingAction) return;
    
    setIsExecutingAction(true);
    
    try {
      const response = await api.post('/ai-assistant/execute-action', {
        action_type: pendingAction.action_type,
        params: pendingAction.params,
        session_id: sessionId
      });
      
      if (response.data.success) {
        toast.success(response.data.message);
        
        // Add success message to chat
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: `✅ **Action Completed Successfully**\n\n${response.data.message}`,
          timestamp: new Date().toISOString(),
          isActionResult: true,
          actionSuccess: true
        }]);
        
        // Clear pending action
        setPendingAction(null);
        
        // Refresh stats
        fetchQuickStats();
      } else {
        throw new Error(response.data.message || 'Action failed');
      }
    } catch (error) {
      console.error('Action execution error:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Failed to execute action';
      toast.error(errorMsg);
      
      // Add error message to chat
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: `❌ **Action Failed**\n\n${errorMsg}`,
        timestamp: new Date().toISOString(),
        isActionResult: true,
        actionSuccess: false
      }]);
    } finally {
      setIsExecutingAction(false);
    }
  };

  // Cancel pending action
  const handleCancelAction = () => {
    setPendingAction(null);
    toast.info('Action cancelled');
    
    setMessages(prev => [...prev, {
      role: 'assistant',
      content: '🚫 Action cancelled. Let me know if you need anything else.',
      timestamp: new Date().toISOString()
    }]);
  };

  // Fetch recommendations
  const fetchRecommendations = async () => {
    try {
      const response = await api.get('/ai-assistant/recommendations');
      if (response.data.recommendations) {
        setRecommendations(response.data.recommendations);
      }
    } catch (error) {
      console.error('Failed to fetch recommendations:', error);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleClearChat = async () => {
    if (!window.confirm('Clear chat history?')) return;
    
    try {
      if (sessionId) {
        await api.delete(`/ai-assistant/history?session_id=${sessionId}`);
      }
      setMessages([]);
      setSessionId(null);
      setContextSuggestions([]);
      setShowSuggestions(true);
      setPendingAction(null);
      setRecommendations([]);
      toast.success('Chat cleared');
    } catch (error) {
      toast.error('Failed to clear chat');
    }
  };

  // Categorized quick questions
  const quickQuestionCategories = [
    {
      title: "📊 Dashboard",
      icon: BarChart3,
      questions: [
        "Show me today's summary",
        "What needs my attention?",
        "Any urgent alerts?"
      ]
    },
    {
      title: "🚚 Drivers",
      icon: Users,
      questions: [
        "Where are all my drivers?",
        "Who's available right now?",
        "Which drivers are close to HOS limits?"
      ]
    },
    {
      title: "📦 Loads",
      icon: Package,
      questions: [
        "Show today's deliveries",
        "Which containers have LFD approaching?",
        "Show loads on hold"
      ]
    },
    {
      title: "🛣️ OTR & Load Board",
      icon: Truck,
      questions: [
        "Show available OTR loads",
        "Find loads from CA to TX",
        "Who can take a 500 mile load?"
      ]
    },
    {
      title: "⏱️ HOS / ELD",
      icon: Clock,
      questions: [
        "Which drivers have HOS warnings?",
        "Show me all HOS violations",
        "Who has enough hours for a long haul?"
      ]
    },
    {
      title: "📍 Local Loads",
      icon: MapPin,
      questions: [
        "Show active local deliveries",
        "Any PODs missing today?",
        "Status of local job LOC-2025-0001"
      ]
    },
    {
      title: "💰 Accounting",
      icon: DollarSign,
      questions: [
        "Generate payout report",
        "Driver pay this month",
        "Revenue report"
      ]
    },
    {
      title: "🚢 Port Intelligence",
      icon: Package,
      questions: [
        "Where can I return my SSL=ONE 40HC empty?",
        "Which terminals are open for empty returns today?",
        "Show me containers at risk of demurrage"
      ]
    },
    {
      title: "📄 D&D Disputes",
      icon: AlertTriangle,
      questions: [
        "Help me open a dispute for a demurrage charge",
        "What evidence do I need to dispute per-diem?",
        "Show me my open D&D disputes"
      ]
    },
    {
      title: "⚡ Quick Actions",
      icon: Zap,
      questions: [
        "Create a new OTR load from LA to Dallas",
        "Assign driver to the next available load",
        "What do you recommend I do next?"
      ]
    }
  ];

  const handleQuickQuestion = (question) => {
    setInputMessage(question);
    setTimeout(() => handleSendMessage(question), 100);
  };

  // Calculate alert count from stats
  const alertCount = quickStats ? (
    (quickStats.lfd_approaching || 0) + 
    (quickStats.containers_on_hold || 0)
  ) : 0;

  // Floating button when closed
  if (!isOpen) {
    return (
      <div className="fixed bottom-6 right-6 z-50">
        <Button
          onClick={() => setIsOpen(true)}
          className="h-14 w-14 rounded-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 shadow-lg shadow-blue-500/25 relative"
          data-testid="ai-assistant-btn"
        >
          <Sparkles className="h-6 w-6" />
          {alertCount > 0 && (
            <span className="absolute -top-1 -right-1 h-5 w-5 bg-red-500 rounded-full text-xs text-white flex items-center justify-center animate-pulse">
              {alertCount}
            </span>
          )}
        </Button>
      </div>
    );
  }

  // Minimized state
  if (isMinimized) {
    return (
      <Card 
        className="fixed bottom-6 right-6 w-72 bg-slate-900 border-slate-700 shadow-xl z-50 cursor-pointer"
        onClick={() => setIsMinimized(false)}
      >
        <div className="flex items-center justify-between p-3">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-full bg-gradient-to-r from-blue-600 to-purple-600 flex items-center justify-center">
              <Bot className="h-4 w-4 text-white" />
            </div>
            <span className="text-white font-medium">Drayly AI</span>
            {alertCount > 0 && (
              <Badge className="bg-red-500/20 text-red-400 border-red-500/50">
                {alertCount} alerts
              </Badge>
            )}
          </div>
          <Maximize2 className="h-4 w-4 text-slate-400" />
        </div>
      </Card>
    );
  }

  // Full chat panel
  return (
    <Card className="fixed bottom-6 right-6 w-[420px] h-[650px] bg-slate-900 border-slate-700 shadow-2xl z-50 flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-slate-800 bg-gradient-to-r from-blue-600/10 to-purple-600/10">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-full bg-gradient-to-r from-blue-600 to-purple-600 flex items-center justify-center">
            <Bot className="h-5 w-5 text-white" />
          </div>
          <div>
            <h3 className="text-white font-semibold flex items-center gap-2">
              Drayly AI
              {alertCount > 0 && (
                <Badge className="bg-red-500/20 text-red-400 text-xs border-red-500/50">
                  {alertCount}
                </Badge>
              )}
            </h3>
            <p className="text-xs text-slate-400">Your intelligent dispatch assistant</p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={fetchQuickStats}
            className="h-8 w-8 p-0 text-slate-400 hover:text-white"
            title="Refresh data"
          >
            <RefreshCw className={`h-4 w-4 ${loadingStats ? 'animate-spin' : ''}`} />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClearChat}
            className="h-8 w-8 p-0 text-slate-400 hover:text-white"
            title="Clear chat"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsMinimized(true)}
            className="h-8 w-8 p-0 text-slate-400 hover:text-white"
            title="Minimize"
          >
            <Minimize2 className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsOpen(false)}
            className="h-8 w-8 p-0 text-slate-400 hover:text-white"
            title="Close"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Quick Stats Bar */}
      {quickStats && messages.length === 0 && (
        <div className="p-3 border-b border-slate-800 bg-slate-800/50">
          <div className="grid grid-cols-4 gap-2">
            <div className="text-center">
              <div className="text-lg font-bold text-blue-400">{quickStats.imports?.total || 0}</div>
              <div className="text-xs text-slate-500">Imports</div>
            </div>
            <div className="text-center">
              <div className="text-lg font-bold text-green-400">{quickStats.exports?.total || 0}</div>
              <div className="text-xs text-slate-500">Exports</div>
            </div>
            <div className="text-center">
              <div className="text-lg font-bold text-amber-400">{quickStats.deliveries_today || 0}</div>
              <div className="text-xs text-slate-500">Deliveries</div>
            </div>
            <div className="text-center">
              <div className="text-lg font-bold text-purple-400">{quickStats.drivers?.total || 0}</div>
              <div className="text-xs text-slate-500">Drivers</div>
            </div>
          </div>
          
          {/* Alerts Section */}
          {(quickStats.lfd_approaching > 0 || quickStats.containers_on_hold > 0) && (
            <div className="mt-2 flex gap-2">
              {quickStats.lfd_approaching > 0 && (
                <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/50 text-xs">
                  <Clock className="h-3 w-3 mr-1" />
                  {quickStats.lfd_approaching} LFD approaching
                </Badge>
              )}
              {quickStats.containers_on_hold > 0 && (
                <Badge className="bg-red-500/20 text-red-400 border-red-500/50 text-xs">
                  <AlertTriangle className="h-3 w-3 mr-1" />
                  {quickStats.containers_on_hold} on hold
                </Badge>
              )}
            </div>
          )}
        </div>
      )}

      {/* Messages */}
      <ScrollArea className="flex-1 p-4">
        {messages.length === 0 ? (
          <div className="space-y-4">
            <div className="text-center py-4">
              <Sparkles className="h-10 w-10 text-blue-400 mx-auto mb-2 opacity-50" />
              <p className="text-slate-400 text-sm">
                Hi {user?.name?.split(' ')[0] || 'there'}! How can I help you today?
              </p>
            </div>
            
            {/* Categorized Quick Questions */}
            <div className="space-y-3">
              {quickQuestionCategories.map((category, catIdx) => (
                <div key={catIdx} className="space-y-1">
                  <p className="text-xs text-slate-500 font-medium flex items-center gap-1">
                    {category.title}
                  </p>
                  <div className="space-y-1">
                    {category.questions.map((q, idx) => (
                      <Button
                        key={idx}
                        variant="outline"
                        size="sm"
                        className="w-full justify-start text-left h-auto py-2 px-3 text-xs border-slate-700 text-slate-300 hover:bg-slate-800 hover:border-blue-500/50"
                        onClick={() => handleQuickQuestion(q)}
                      >
                        <ChevronRight className="h-3 w-3 mr-2 text-slate-500" />
                        {q}
                      </Button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {messages.map((msg, idx) => (
              <div
                key={idx}
                className={`flex gap-3 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                {msg.role === 'assistant' && (
                  <div className={`h-8 w-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                    msg.isActionResult 
                      ? msg.actionSuccess 
                        ? 'bg-green-600' 
                        : 'bg-red-600'
                      : 'bg-gradient-to-r from-blue-600 to-purple-600'
                  }`}>
                    {msg.isActionResult ? (
                      msg.actionSuccess ? <CheckCircle className="h-4 w-4 text-white" /> : <XCircle className="h-4 w-4 text-white" />
                    ) : (
                      <Bot className="h-4 w-4 text-white" />
                    )}
                  </div>
                )}
                <div
                  className={`max-w-[85%] rounded-lg p-3 ${
                    msg.role === 'user'
                      ? 'bg-blue-600 text-white'
                      : msg.isError
                      ? 'bg-red-900/50 text-red-200'
                      : msg.isActionResult
                      ? msg.actionSuccess
                        ? 'bg-green-900/30 border border-green-500/30 text-green-200'
                        : 'bg-red-900/30 border border-red-500/30 text-red-200'
                      : 'bg-slate-800 text-slate-200'
                  }`}
                >
                  {msg.role === 'assistant' ? (
                    <div className="prose prose-sm prose-invert max-w-none">
                      <ReactMarkdown components={MARKDOWN_COMPONENTS}>
                        {msg.content}
                      </ReactMarkdown>
                      
                      {/* Show Action Confirmation Card if this message has a proposed action */}
                      {msg.proposedAction && pendingAction && msg === messages[messages.length - 1] && (
                        <ActionConfirmationCard
                          action={msg.proposedAction}
                          onConfirm={handleExecuteAction}
                          onCancel={handleCancelAction}
                          isExecuting={isExecutingAction}
                        />
                      )}
                      
                      {/* Show Recommendations if available */}
                      {msg.recommendations && msg.recommendations.length > 0 && (
                        <RecommendationsCard 
                          recommendations={msg.recommendations}
                          onAsk={handleQuickQuestion}
                        />
                      )}
                    </div>
                  ) : (
                    <p className="text-sm">{msg.content}</p>
                  )}
                </div>
                {msg.role === 'user' && (
                  <div className="h-8 w-8 rounded-full bg-slate-700 flex items-center justify-center flex-shrink-0">
                    <User className="h-4 w-4 text-slate-300" />
                  </div>
                )}
              </div>
            ))}
            
            {/* Pending Action Card (shown at bottom if still pending) */}
            {pendingAction && !isLoading && messages.length > 0 && !messages[messages.length - 1]?.proposedAction && (
              <div className="mt-4">
                <ActionConfirmationCard
                  action={pendingAction}
                  onConfirm={handleExecuteAction}
                  onCancel={handleCancelAction}
                  isExecuting={isExecutingAction}
                />
              </div>
            )}
            
            {/* Context-aware suggestions */}
            {contextSuggestions.length > 0 && !isLoading && !pendingAction && (
              <div className="flex flex-wrap gap-2 mt-2">
                {contextSuggestions.map((suggestion, idx) => (
                  <Button
                    key={idx}
                    variant="outline"
                    size="sm"
                    className="text-xs h-7 border-slate-700 text-slate-400 hover:text-white hover:border-blue-500/50"
                    onClick={() => handleQuickQuestion(suggestion)}
                  >
                    {suggestion}
                  </Button>
                ))}
              </div>
            )}
            
            {isLoading && (
              <div className="flex gap-3">
                <div className="h-8 w-8 rounded-full bg-gradient-to-r from-blue-600 to-purple-600 flex items-center justify-center flex-shrink-0">
                  <Bot className="h-4 w-4 text-white" />
                </div>
                <div className="bg-slate-800 rounded-lg p-3">
                  <div className="flex items-center gap-2">
                    <div className="flex gap-1">
                      <span className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{animationDelay: '0ms'}}></span>
                      <span className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{animationDelay: '150ms'}}></span>
                      <span className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{animationDelay: '300ms'}}></span>
                    </div>
                    <span className="text-sm text-slate-400">Analyzing your request...</span>
                  </div>
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>
        )}
      </ScrollArea>

      {/* Input */}
      <div className="p-4 border-t border-slate-800">
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="icon"
            onClick={toggleVoiceInput}
            className={`border-slate-700 ${isListening ? 'bg-red-500/20 border-red-500 text-red-400' : 'text-slate-400'}`}
            title={isListening ? 'Stop listening' : 'Voice input'}
            data-testid="ai-voice-btn"
          >
            {isListening ? (
              <MicOff className="h-4 w-4" />
            ) : (
              <Mic className="h-4 w-4" />
            )}
          </Button>
          <Input
            ref={inputRef}
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder={isListening ? "Listening..." : "Ask me anything..."}
            className="flex-1 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
            disabled={isLoading}
            data-testid="ai-input"
          />
          <Button
            onClick={() => handleSendMessage()}
            disabled={!inputMessage.trim() || isLoading}
            className="bg-blue-600 hover:bg-blue-500"
            data-testid="ai-send-btn"
          >
            {isLoading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
          </Button>
        </div>
        <p className="text-xs text-slate-600 mt-2 text-center">
          Press Enter to send • Voice input available
        </p>
      </div>
    </Card>
  );
};

export default AIAssistant;
